import {
    CheckOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Checkbox, Col, Flex, Pagination, Popconfirm, Row, Skeleton, Tag, theme, Typography,
} from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import {
    allCommentsQueries, scriptCommentsMutations,
} from '@/api';
import {
    UserRole, UserRoles,
} from '@/constants';
import { AllCommentItem } from '@/types';

const PER_PAGE = 15;

const AllCommentCard = ({ comment }: { comment: AllCommentItem }) => {
    const { token } = theme.useToken();

    const scriptId = comment.script?.id ?? '';

    const { mutate: markRead, isPending: isMarkingRead } = useMutation(
        scriptCommentsMutations.markRead(scriptId, comment.id),
    );

    const scriptPath = comment.script?.project_id
        ? `/projects/${comment.script.project_id}/scripts/${comment.script.id}`
        : null;

    const roleName = comment.user?.role
        ? (UserRoles[comment.user.role as UserRole] ?? comment.user.role)
        : null;

    return (
        <Card
            size="small"
            style={{ marginBottom: token.marginMD }}
            styles={{ body: { padding: token.paddingMD } }}
        >
            <Flex vertical gap="small">
                <Flex justify="space-between" align="flex-start" gap="small" wrap="wrap">
                    <Flex vertical gap={2}>
                        {scriptPath ? (
                            <Link to={scriptPath}>
                                <Typography.Text strong style={{ color: token.colorPrimary }}>
                                    {comment.script?.title ?? 'Скрипт'}
                                </Typography.Text>
                            </Link>
                        ) : (
                            <Typography.Text strong>{comment.script?.title ?? 'Скрипт'}</Typography.Text>
                        )}
                        {comment.script?.project_title && (
                            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                                {comment.script.project_title}
                            </Typography.Text>
                        )}
                    </Flex>

                    <Flex gap="small" align="center" wrap="wrap">
                        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                            {comment.created_at}
                        </Typography.Text>
                        {comment.is_edited && (
                            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM - 1 }}>
                                (редаговано)
                            </Typography.Text>
                        )}
                        {comment.is_read ? (
                            <Tag color="success" style={{ margin: 0 }}>прочитано</Tag>
                        ) : (
                            <Tag style={{ margin: 0 }}>не прочитано</Tag>
                        )}
                        {!comment.is_read && (
                            <Popconfirm
                                title="Позначити як прочитано?"
                                description="Після цього автор не зможе редагувати або видалити коментар."
                                okText="Так"
                                cancelText="Скасувати"
                                onConfirm={() => markRead()}
                            >
                                <Button
                                    size="small"
                                    icon={<CheckOutlined />}
                                    loading={isMarkingRead}
                                >
                                    Прочитано
                                </Button>
                            </Popconfirm>
                        )}
                    </Flex>
                </Flex>

                <Flex gap="small" align="center" wrap="wrap">
                    <Typography.Text strong style={{ fontSize: token.fontSizeSM }}>
                        {comment.user?.name ?? 'Невідомий'}
                    </Typography.Text>
                    {roleName && (
                        <Tag style={{ margin: 0, fontSize: token.fontSizeSM - 1 }}>{roleName}</Tag>
                    )}
                </Flex>

                <Typography.Paragraph
                    style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    {comment.body}
                </Typography.Paragraph>
            </Flex>
        </Card>
    );
};

export const CommentsPage = () => {
    const { token } = theme.useToken();
    const [page, setPage] = useState(1);
    const [hideRead, setHideRead] = useState(true);

    const { data, isLoading } = useQuery(allCommentsQueries.getAll(page, PER_PAGE, hideRead));

    const comments = data?.data ?? [];
    const total = data?.meta?.total ?? 0;

    const handleHideReadChange = (checked: boolean) => {
        setHideRead(checked);
        setPage(1);
    };

    return (
        <>
            <Helmet>
                <title>Коментарі</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={16} xl={14}>
                    <Flex justify="space-between" align="center" style={{ marginBottom: token.marginLG }} wrap="wrap" gap="small">
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Коментарі
                        </Typography.Title>
                        <Checkbox
                            checked={hideRead}
                            onChange={(e) => handleHideReadChange(e.target.checked)}
                        >
                            Сховати прочитані
                        </Checkbox>
                    </Flex>

                    {isLoading && (
                        <Flex vertical gap="middle">
                            {[1, 2, 3].map((i) => (
                                <Card key={i} size="small">
                                    <Skeleton active paragraph={{ rows: 2 }} />
                                </Card>
                            ))}
                        </Flex>
                    )}

                    {!isLoading && comments.length === 0 && (
                        <Card>
                            <Typography.Text type="secondary">
                                {hideRead ? 'Немає непрочитаних коментарів' : 'Коментарів ще немає'}
                            </Typography.Text>
                        </Card>
                    )}

                    {!isLoading && comments.length > 0 && (
                        <>
                            {comments.map((comment) => (
                                <AllCommentCard key={comment.id} comment={comment} />
                            ))}

                            {total > PER_PAGE && (
                                <Flex justify="center" style={{ marginTop: token.marginMD }}>
                                    <Pagination
                                        current={page}
                                        pageSize={PER_PAGE}
                                        total={total}
                                        onChange={setPage}
                                        showSizeChanger={false}
                                    />
                                </Flex>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        </>
    );
};
